// =============================================================================
// useFocusRefetch.js, fire a silent refetch when the tab returns to focus
// =============================================================================
//
// Pairs with `lib/pageCache.js`:
//   * pageCache makes the *initial paint* after a Safari tab-suspension remount
//     instant (hydrated from sessionStorage).
//   * useFocusRefetch makes the data *fresh again* shortly after, without
//     ever blanking the UI to a skeleton.
//
// Behaviour:
//   * Tracks how long the tab has been hidden via `document.visibilitychange`.
//   * When the tab becomes visible again AND was hidden for at least
//     `minHiddenMs` (default 60s), calls `handler()`.
//   * The handler must be *silent*, it should refetch in the background and
//     update state on success, NOT toggle a loading flag.
//   * Below the threshold (quick tab flicks), does nothing. This avoids
//     thrashing on the user's natural alt-tab cadence.
//
// Why 60s default:
//   Token refreshes and Safari's heap eviction both tend to happen ~1 minute
//   in. Below that the in-memory state is almost certainly still fresh.

import { useEffect, useRef } from 'react';

/**
 * @param {() => void | Promise<void>} handler  Silent refetch fn.
 * @param {object} [opts]
 * @param {number} [opts.minHiddenMs=60000]  Only fire if hidden ≥ this long.
 * @param {boolean} [opts.enabled=true]      Toggle without unmounting.
 */
export default function useFocusRefetch(handler, { minHiddenMs = 60_000, enabled = true } = {}) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        const start = hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (start == null) return; // never hidden, initial fire
        const hiddenFor = Date.now() - start;
        if (hiddenFor >= minHiddenMs) {
          try {
            const result = handlerRef.current?.();
            // Swallow any error, handler is expected to manage its own.
            if (result && typeof result.catch === 'function') result.catch(() => {});
          } catch (_) {
            /* noop */
          }
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    // Also fire on `pageshow` for Safari's bfcache restore path, this fires
    // when a page is restored from the back-forward cache without a remount.
    const onPageShow = (e) => {
      if (e.persisted) {
        // bfcache restore, treat as "long enough" focus return.
        try {
          const result = handlerRef.current?.();
          if (result && typeof result.catch === 'function') result.catch(() => {});
        } catch (_) { /* noop */ }
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled, minHiddenMs]);
}
